import { useState } from 'react';
import { QrCode, Share2, Download, Mail, MessageCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export const BusinessCardSection = () => {
  const [selectedCard, setSelectedCard] = useState(2);

  const sampleCards = [
    {
      name: 'Ahmed Al Mansoori',
      title: 'HR Manager',
      company: 'hirerkey',
      email: 'ahmed@hirerkey.ae',
      phone: '+971 50 123 4567',
      color: 'from-blue-600 to-blue-700',
    },
    {
      name: 'Sarah Johnson',
      title: 'Operations Director',
      company: 'hirerkey',
      email: 'sarah@hirerkey.ae',
      phone: '+971 50 234 5678',
      color: 'from-purple-600 to-purple-700',
    },
    {
      name: 'Fatima Hassan',
      title: 'Recruitment Lead',
      company: 'hirerkey',
      email: 'fatima@hirerkey.ae',
      phone: '+971 50 345 6789',
      color: 'from-teal-600 to-teal-700',
    },
  ];

  const currentCard = sampleCards[selectedCard];

  return (
    <section className="py-8 sm:py-10 md:py-12 bg-gradient-subtle relative overflow-hidden">
      {/* Decorative Background with GPU optimization */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl will-change-transform"></div>
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl will-change-transform"></div>
      </div>

      <div className="relative max-w-[745px] w-full mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 animate-fade-in">
          <Badge className="mb-3 bg-accent/10 text-accent border-accent/20 text-xs">
            Unique Feature
          </Badge>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
            <span className="emphasize">Digital Business Cards</span> That Work
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            Generate beautiful, branded business cards in seconds. Share via QR code, WhatsApp, or email. 
            <span className="emphasize-accent font-semibold text-foreground"> No app required</span>—just scan and save.
          </p>
        </div>

        {/* Static Card Demo - Single Column */}
        <div className="space-y-6 sm:space-y-8 animate-fade-in">
          {/* Static Business Card */}
          <div className="relative max-w-2xl mx-auto">
            <Card className={`bg-gradient-to-br ${currentCard.color} text-white p-6 sm:p-8 shadow-md`}>
              <div className="flex justify-between items-start gap-6">
                {/* Left side - Logo and details */}
                <div className="flex-1 flex flex-col justify-between h-full">
                  {/* Logo */}
                  <div className="mb-6">
                    <img
                      src="https://customer-assets.emergentagent.com/job_profile-audit/artifacts/daspifu4_logo-dark.png"
                      alt="hirerkey"
                      className="w-auto"
                      style={{ height: '1.4rem' }}
                    />
                  </div>

                  {/* Contact Details */}
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-2xl sm:text-3xl font-bold mb-1">{currentCard.name}</h3>
                      <p className="text-white/90 text-base sm:text-lg">{currentCard.title}</p>
                    </div>
                    <div className="space-y-1 text-sm text-white/80">
                      <p>{currentCard.email}</p>
                      <p>{currentCard.phone}</p>
                    </div>
                  </div>
                </div>

                {/* Right side - QR Code */}
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 sm:w-36 sm:h-36 bg-white rounded-xl flex items-center justify-center p-3">
                    <QrCode className="w-full h-full text-gray-800" />
                  </div>
                  <p className="text-xs text-center mt-2 text-white/70">Scan to save</p>
                </div>
              </div>

              {/* Bottom Address */}
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-xs text-white/70">
                  Meydan Grandstand, Meydan Road, Nad Al Sheba, Dubai, UAE
                </p>
              </div>
            </Card>
          </div>

          {/* Card Selector */}
          <div className="flex justify-center space-x-3">
            {sampleCards.map((card, index) => (
              <button
                key={index}
                onClick={() => setSelectedCard(index)}
                className={`w-12 h-8 rounded-lg bg-gradient-to-br ${card.color} transition-all ${
                  selectedCard === index ? 'ring-4 ring-primary/30 scale-110' : 'opacity-50 hover:opacity-75'
                }`}
                aria-label={`Select ${card.name}'s card`}
              />
            ))}
          </div>
        </div>

        {/* Features List - Below Card */}
        <div className="space-y-4 sm:space-y-6 animate-fade-in pt-6 sm:pt-8">
          <div className="space-y-4">
              {[
                {
                  icon: QrCode,
                  title: 'QR Code Sharing',
                  description: 'Each card includes a unique QR code. Recipients scan to save contact details automatically.',
                },
                {
                  icon: Share2,
                  title: 'Multiple Share Options',
                  description: 'Share via WhatsApp, Email, or download as PNG. Perfect for networking events.',
                },
                {
                  icon: Download,
                  title: 'HR-Managed',
                  description: 'HR issues cards from admin dashboard. Cards match your company branding automatically.',
                },
              ].map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="flex space-x-3 p-3 rounded-lg hover:bg-muted/30 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-0.5" style={{ fontSize: '1.2rem' }}>{feature.title}</h3>
                      <p className="text-muted-foreground leading-relaxed" style={{ fontSize: '0.8rem' }}>
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Share Buttons Demo */}
            <Card className="p-4 bg-muted/30 border-dashed">
              <p className="text-xs font-medium mb-3">Share Options:</p>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" className="flex items-center space-x-1 text-[10px] h-7 px-2">
                  <MessageCircle className="w-2.5 h-2.5" />
                  <span>WhatsApp</span>
                </Button>
                <Button variant="outline" size="sm" className="flex items-center space-x-1 text-[10px] h-7 px-2">
                  <Mail className="w-2.5 h-2.5" />
                  <span>Email</span>
                </Button>
                <Button variant="outline" size="sm" className="flex items-center space-x-1 text-[10px] h-7 px-2">
                  <Download className="w-2.5 h-2.5" />
                  <span>Download</span>
                </Button>
              </div>
            </Card>
          </div>
      </div>

    </section>
  );
};

export default BusinessCardSection;
